/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloProvider,
  from,
  Observable,
  FetchResult,
  ServerError,
  ServerParseError,
  ApolloError,
  Operation,
  NextLink,
} from "@apollo/client";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { useAuth0 } from "@auth0/auth0-react";
import { GraphQLError } from "graphql";
import PossibleTypesResult from "~/generated/possibleTypes";
import { NODE_ENV } from "~/config";
import { getPeriod, getCustomer } from "~/services/customers";
import { CostAnalysisPolicies } from "~/views/applications/cacheTypePolicies";
import { FinopsKpiPolicies } from "~/views/finopsKpi/cacheTypePolicies";

if (NODE_ENV === "development" || NODE_ENV === "test") {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

function customFetch(uri: RequestInfo, options: RequestInit) {
  const { operationName = "" } = JSON.parse(options.body?.toString() ?? "{}");

  const fetchUrl = `${uri}?opname=${operationName}`;
  return fetch(fetchUrl, options);
}

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  fetch: customFetch,
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      // Retry enabled for cost analysis queries,
      // because in some cases (especially longer date periods) it timeouts due to unknown reasons (that is not related to server load).
      // It is a temporary fix for issue: NPS-1805, new ticket to find a cause of the timeouts created: KLAR-9245.
      if (costAnalysisQueries.includes(operation.operationName)) {
        return true;
      }

      // Prevent retries when server is stuck, don't send additional load to handle
      if (error?.statusCode === 503) {
        return false;
      }

      return !!error;
    },
  },
});

export const cache = new InMemoryCache({
  typePolicies: {
    // Additional configuration for typename `BusinessContextKpis`. This object doesn't have any `id` to track
    // it inside cache, so it's replaced every time causing duplicate queries.
    // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#configuring-merge-functions-for-types-rather-than-fields
    ...FinopsKpiPolicies,
    ...CostAnalysisPolicies,
    Query: {
      fields: {
        // Below queries are not paginated, we set merge strategy explicitly to avoid duplications
        people: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
        discoveryRules: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
  possibleTypes: PossibleTypesResult.possibleTypes,
});

function isServerError(
  value: ApolloError["networkError"] | undefined
): value is ServerError | ServerParseError {
  return value != null && "statusCode" in value;
}

export function CustomApolloProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getAccessTokenSilently } = useAuth0();

  // Updater function is not used
  // eslint-disable-next-line react/hook-use-state
  const [client] = useState(() => {
    const getIdToken = async () => {
      try {
        const { id_token } = await getAccessTokenSilently({
          cacheMode: "off",
          detailedResponse: true,
        });

        return id_token;
      } catch {
        // Refresh token is expired
        window.location.reload();
      }
    };

    const resolveOperation = (operation: Operation, forward: NextLink) => {
      return new Observable<FetchResult>((observer) => {
        // Async observer is not supported, IIFE is used as a workaround
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async () => {
          try {
            const idToken = await getIdToken();

            if (!idToken) {
              throw new GraphQLError("Empty ID Token");
            }

            // Retry the failed request
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            forward(operation).subscribe(subscriber);
          } catch (error) {
            observer.error(error);
          }
        })();
      });
    };

    const errorLink = onError(
      ({ graphQLErrors = [], networkError, operation, forward }) => {
        for (const err of graphQLErrors) {
          if (err?.extensions?.code === "UNAUTHENTICATED") {
            return resolveOperation(operation, forward);
          }
        }

        // 403 Forbidden is returned when the id_token is no longer valid
        if (isServerError(networkError) && networkError.statusCode === 403) {
          return resolveOperation(operation, forward);
        }
      }
    );

    const authLink = setContext(
      // @ts-expect-error TODO: fix
      async (_, { headers }: { headers: Record<string, unknown> }) => {
        try {
          // Get saved token or refresh it in case it expired
          const { id_token } = await getAccessTokenSilently({
            detailedResponse: true,
          });

          const customer = getCustomer();
          const period = getPeriod();

          return {
            headers: {
              Authorization: id_token,
              "x-customer-id": customer?.id || "",
              "x-billing-period": period?.period || "",
              ...headers,
            },
          };
        } catch {
          window.location.reload();
        }
      }
    );

    return new ApolloClient({
      cache,
      link: from([authLink, errorLink, retryLink, httpLink]),
      name: "overwatch",
      defaultOptions: {
        query: {
          errorPolicy: "all",
        },
      },
    });
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}

const costAnalysisQueries = [
  "ApplicationsCostPerCategory",
  "ApplicationsCostPerService",
  "ApplicationsCostAnalysis",
  "AccumulatedCost",
];
